/**
 * TileTagger — User-facing system for building TileMeta from any tileset.
 *
 * Different artists arrange tiles differently. There is NO standard layout.
 * A 47-blob water set from one artist might be a 7×7 grid, another might
 * scatter the pieces randomly. We can't assume layout — we need per-tile tagging.
 *
 * The TileTagger lets users teach the system about their tiles through
 * a series of structured operations:
 *
 * 1. TAG INDIVIDUAL TILES — assign a role (terrain, wall, floor, decoration, etc.)
 * 2. GROUP TERRAIN TILES — select tiles and say "these are all grass variants"
 * 3. MAP AUTOTILE VARIANTS — for a terrain group, assign which tile is which bitmask
 * 4. DEFINE MULTI-TILE OBJECTS — select a rectangle of tiles, name it, set placement rules
 * 5. DEFINE WALL SETS — tag directional wall tiles (top, bottom, left, right, corners)
 * 6. DEFINE FLOOR TYPES — tag floor tiles and assign them to room zones
 *
 * Every operation is recorded as an "action" that can be undone, replayed,
 * or serialized. The final result is a TileMeta JSON that gets saved alongside
 * the tileset and used by all generators.
 *
 * Training data is persistent — once a user tags a tileset, it stays tagged.
 * Tags can be shared: export a TileMeta JSON, share it, others import it
 * for the same tileset.
 */

import { TileMeta, ROLE, AUTOTILE_4BIT, AUTOTILE_47, AUTOTILE_ROAD, AUTOTILE_NONE, BLOB_47_MASKS } from './TileMeta.js';

export class TileTagger {
  /**
   * @param {Object} tilesetInfo — Basic tileset properties
   * @param {string} tilesetInfo.id
   * @param {string} tilesetInfo.name
   * @param {number} tilesetInfo.tileSize
   * @param {number} tilesetInfo.columns
   * @param {number} tilesetInfo.tileCount
   * @param {Object} [existingMeta] — Previously saved TileMeta JSON to resume editing
   */
  constructor(tilesetInfo, existingMeta = null) {
    this.tilesetInfo = tilesetInfo;
    this._undoHistory = [];
    this._redoHistory = [];
    this._attribution = tilesetInfo.attribution || null;

    // Working data — start from existing or empty
    this._terrainGroups = new Map();
    this._wallSets = new Map();
    this._floorTypes = new Map();
    this._objects = new Map();
    this._decorations = new Map();
    this._openings = new Map();
    this._tileRoles = new Map();
    this._tileTags = new Map();

    if (existingMeta) {
      this._loadExisting(existingMeta);
    }
  }

  // ─── Core Tagging Operations ──────────────────────────────────

  /**
   * Tag a single tile with a role.
   * @param {number} tileId
   * @param {string} role — From ROLE constants
   * @param {string[]} [tags] — Optional free-form tags
   */
  tagTile(tileId, role, tags = []) {
    this._beforeMutate();
    this._tileRoles.set(tileId, role);
    if (tags.length > 0) {
      const existing = this._tileTags.get(tileId) || new Set();
      for (const t of tags) existing.add(t);
      this._tileTags.set(tileId, existing);
    }
  }

  /**
   * Tag multiple tiles at once (single undo action).
   */
  tagTiles(tileIds, role, tags = []) {
    this._beforeMutate();
    for (const id of tileIds) {
      this._tileRoles.set(id, role);
      if (tags.length > 0) {
        const existing = this._tileTags.get(id) || new Set();
        for (const t of tags) existing.add(t);
        this._tileTags.set(id, existing);
      }
    }
  }

  /**
   * Define a terrain group from a set of tiles.
   * @param {Object} group — TerrainGroup definition
   */
  defineTerrainGroup(group) {
    this._beforeMutate();
    this._terrainGroups.set(group.id, { ...group });
    if (group.centerTile != null) this._tileRoles.set(group.centerTile, ROLE.TERRAIN);
    for (const tileId of Object.values(group.variants || {})) {
      this._tileRoles.set(tileId, ROLE.TERRAIN);
    }
    for (const tileId of (group.randomVariants || [])) {
      this._tileRoles.set(tileId, ROLE.TERRAIN);
    }
  }

  /**
   * Convenience: define a simple terrain (no autotiling, just center + variants).
   */
  defineSimpleTerrain(id, centerTile, variants = []) {
    this.defineTerrainGroup({
      id,
      autotileFormat: AUTOTILE_NONE,
      centerTile,
      variants: {},
      randomVariants: variants,
    });
  }

  /**
   * Define a 47-blob terrain group.
   * User provides a mapping of blob index (0–46) to tile ID.
   * Incomplete mappings are fine — missing shapes fall back to center tile.
   *
   * @param {string} id
   * @param {number} centerTile — The fully-surrounded center variant
   * @param {Object<number,number>} blobIndexToTile — { 0: tileId, 1: tileId, ... 46: tileId }
   * @param {Object} [options] — { transitionsTo, randomVariants, animated, animFrames, animSpeed }
   */
  defineBlobTerrain(id, centerTile, blobIndexToTile, options = {}) {
    // Convert blob index → bitmask → tile
    const variants = {};
    for (const [index, tileId] of Object.entries(blobIndexToTile)) {
      const bitmask = BLOB_47_MASKS[Number(index)];
      if (bitmask != null) variants[bitmask] = tileId;
    }

    this.defineTerrainGroup({
      id,
      autotileFormat: AUTOTILE_47,
      centerTile,
      variants,
      transitionsTo: options.transitionsTo || [],
      randomVariants: options.randomVariants || [],
      animated: options.animated || false,
      animFrames: options.animFrames || [],
      animSpeed: options.animSpeed || 200,
    });
  }

  /**
   * Define a road network terrain.
   * @param {string} id
   * @param {Object} shapes — { STRAIGHT_H: tileId, STRAIGHT_V: tileId, CURVE_NE: tileId, ... }
   */
  defineRoadTerrain(id, shapes, options = {}) {
    this.defineTerrainGroup({
      id,
      autotileFormat: AUTOTILE_ROAD,
      centerTile: shapes.CROSS || shapes.STRAIGHT_H || 0,
      variants: shapes,
      transitionsTo: options.transitionsTo || [],
    });
  }

  /**
   * Define a wall set.
   * @param {Object} wallSet — WallSet definition
   */
  defineWallSet(wallSet) {
    this._beforeMutate();
    this._wallSets.set(wallSet.id, { ...wallSet });
    for (const tileId of Object.values(wallSet)) {
      if (typeof tileId === 'number') this._tileRoles.set(tileId, ROLE.WALL);
    }
  }

  /**
   * Define a floor type.
   * @param {Object} floorType — FloorType definition
   */
  defineFloorType(floorType) {
    this._beforeMutate();
    this._floorTypes.set(floorType.id, { ...floorType });
    this._tileRoles.set(floorType.tile, ROLE.FLOOR);
    for (const v of (floorType.variants || [])) {
      this._tileRoles.set(v, ROLE.FLOOR);
    }
  }

  /**
   * Define a multi-tile object by selecting a rectangle from the tileset.
   *
   * @param {string} id — Object name
   * @param {number} startTile — Top-left tile ID in the tileset
   * @param {number} w — Width in tiles
   * @param {number} h — Height in tiles
   * @param {Object} placement — { zones, wallAlign, clearance, minRoom }
   * @param {Object} [options] — { blocking, clippable, weight }
   */
  defineObject(id, startTile, w, h, placement, options = {}) {
    this._beforeMutate();
    const tiles = [];
    const cols = this.tilesetInfo.columns;

    for (let row = 0; row < h; row++) {
      const tileRow = [];
      for (let col = 0; col < w; col++) {
        const tileId = startTile + row * cols + col;
        tileRow.push(tileId);
        this._tileRoles.set(tileId, ROLE.OBJECT);
      }
      tiles.push(tileRow);
    }

    const obj = {
      id,
      tiles,
      footprint: { w, h },
      blocking: options.blocking ?? true,
      placement: placement || {},
      clippable: options.clippable ?? false,
      weight: options.weight ?? 1,
    };

    this._objects.set(id, obj);
  }

  /**
   * Define a single-tile decoration.
   */
  defineDecoration(id, tileId, zones, options = {}) {
    this._beforeMutate();
    const dec = {
      id,
      tile: tileId,
      blocking: options.blocking ?? false,
      zones,
      indoor: options.indoor ?? true,
      outdoor: options.outdoor ?? false,
      scatter: options.scatter ?? false,
      weight: options.weight ?? 1,
    };

    this._decorations.set(id, dec);
    this._tileRoles.set(tileId, ROLE.DECORATION);
  }

  /**
   * Define a door or window.
   */
  defineOpening(id, type, tileId, options = {}) {
    this._beforeMutate();
    const op = {
      id,
      type,
      tile: tileId,
      tileOpen: options.tileOpen ?? null,
      tileBroken: options.tileBroken ?? null,
      orientation: options.orientation || 'any',
    };

    this._openings.set(id, op);
    this._tileRoles.set(tileId, ROLE.OPENING);
  }

  // ─── Undo / Redo (snapshot-based) ─────────────────────────────
  //
  // Before each mutation, we snapshot the entire state (all Maps + attribution).
  // Undo = restore the snapshot. Redo = restore the post-mutation state.
  // This is correct by construction — no side effects can be missed,
  // because we restore the complete state, not individual fields.
  //
  // Memory cost: ~one Map clone per operation. For typical tilesets (<5000 tiles),
  // each snapshot is <100KB. 100 operations = <10MB. Acceptable.

  /** Take a snapshot of all mutable state. */
  _snapshot() {
    return {
      terrainGroups: new Map(this._terrainGroups),
      wallSets: new Map(this._wallSets),
      floorTypes: new Map(this._floorTypes),
      objects: new Map(this._objects),
      decorations: new Map(this._decorations),
      openings: new Map(this._openings),
      tileRoles: new Map(this._tileRoles),
      tileTags: new Map([...this._tileTags.entries()].map(([k, v]) => [k, new Set(v)])),
      attribution: this._attribution ? { ...this._attribution } : null,
    };
  }

  /** Restore state from a snapshot. */
  _restore(snap) {
    this._terrainGroups = snap.terrainGroups;
    this._wallSets = snap.wallSets;
    this._floorTypes = snap.floorTypes;
    this._objects = snap.objects;
    this._decorations = snap.decorations;
    this._openings = snap.openings;
    this._tileRoles = snap.tileRoles;
    this._tileTags = snap.tileTags;
    this._attribution = snap.attribution;
  }

  /** Save a snapshot before mutation. Call this at the start of every mutating method. */
  _beforeMutate() {
    this._undoHistory.push(this._snapshot());
    this._redoHistory = []; // new action clears redo
  }

  undo() {
    if (this._undoHistory.length === 0) return false;
    // Save current state as redo point
    this._redoHistory.push(this._snapshot());
    // Restore previous state
    this._restore(this._undoHistory.pop());
    return true;
  }

  redo() {
    if (this._redoHistory.length === 0) return false;
    // Save current state as undo point
    this._undoHistory.push(this._snapshot());
    // Restore redo state
    this._restore(this._redoHistory.pop());
    return true;
  }

  /** Number of available undo steps. */
  get undoCount() { return this._undoHistory.length; }

  /** Number of available redo steps. */
  get redoCount() { return this._redoHistory.length; }

  // ─── Build Final TileMeta ─────────────────────────────────────

  /**
   * Set or update attribution/licensing info for this tileset.
   * @param {Object} attribution — { artist, purchaseUrl, licenseType, licenseUrl, website, notes }
   */
  setAttribution(attribution) {
    this._beforeMutate();
    this._attribution = { ...attribution };
  }

  /**
   * Build a TileMeta from all the tagging work done so far.
   * @returns {TileMeta}
   */
  build() {
    return new TileMeta({
      id: this.tilesetInfo.id,
      name: this.tilesetInfo.name,
      tileSize: this.tilesetInfo.tileSize,
      columns: this.tilesetInfo.columns,
      tileCount: this.tilesetInfo.tileCount,
      attribution: this._attribution,
      terrainGroups: [...this._terrainGroups.values()],
      wallSets: [...this._wallSets.values()],
      floorTypes: [...this._floorTypes.values()],
      objects: [...this._objects.values()],
      decorations: [...this._decorations.values()],
      openings: [...this._openings.values()],
      tileRoles: Object.fromEntries(this._tileRoles),
      tileTags: Object.fromEntries(
        [...this._tileTags.entries()].map(([id, tags]) => [id, [...tags]])
      ),
    });
  }

  /**
   * Export the raw tagging data as JSON (for saving/sharing).
   */
  toJSON() {
    return this.build().toJSON();
  }

  // ─── Progress / Coverage ──────────────────────────────────────

  /**
   * How complete is the tagging? Returns coverage stats.
   */
  getCoverage() {
    const total = this.tilesetInfo.tileCount;
    const tagged = this._tileRoles.size;
    const terrainTiles = [...this._tileRoles.entries()].filter(([, r]) => r === ROLE.TERRAIN).length;
    const wallTiles = [...this._tileRoles.entries()].filter(([, r]) => r === ROLE.WALL).length;
    const floorTiles = [...this._tileRoles.entries()].filter(([, r]) => r === ROLE.FLOOR).length;
    const objectTiles = [...this._tileRoles.entries()].filter(([, r]) => r === ROLE.OBJECT).length;
    const decorationTiles = [...this._tileRoles.entries()].filter(([, r]) => r === ROLE.DECORATION).length;

    return {
      total,
      tagged,
      untagged: total - tagged,
      percent: total > 0 ? Math.round((tagged / total) * 100) : 0,
      byRole: {
        terrain: terrainTiles,
        wall: wallTiles,
        floor: floorTiles,
        object: objectTiles,
        decoration: decorationTiles,
      },
      terrainGroups: this._terrainGroups.size,
      wallSets: this._wallSets.size,
      floorTypes: this._floorTypes.size,
      objects: this._objects.size,
      decorations: this._decorations.size,
      openings: this._openings.size,
    };
  }

  /**
   * Get list of untagged tile IDs (tiles with no assigned role).
   */
  getUntaggedTiles() {
    const untagged = [];
    for (let i = 0; i < this.tilesetInfo.tileCount; i++) {
      if (!this._tileRoles.has(i)) untagged.push(i);
    }
    return untagged;
  }

  /**
   * Get what the generator still needs to produce maps.
   * Returns a list of requirements that haven't been met yet.
   */
  getMissingRequirements() {
    const missing = [];

    if (this._terrainGroups.size === 0) {
      missing.push({ type: 'terrain', message: 'No terrain groups defined. Tag at least one ground terrain (grass, dirt, etc.).' });
    }

    if (this._wallSets.size === 0) {
      missing.push({ type: 'wall', message: 'No wall sets defined. Tag wall tiles with directional variants.' });
    }

    if (this._floorTypes.size === 0) {
      missing.push({ type: 'floor', message: 'No floor types defined. Tag at least one interior floor tile.' });
    }

    if (this._openings.size === 0) {
      missing.push({ type: 'opening', message: 'No doors or windows defined.' });
    }

    // Check terrain groups have enough variants for quality autotiling
    for (const [id, tg] of this._terrainGroups) {
      if (tg.autotileFormat === AUTOTILE_47 && Object.keys(tg.variants || {}).length < 16) {
        missing.push({
          type: 'terrain_incomplete',
          message: `Terrain "${id}" uses 47-blob format but only ${Object.keys(tg.variants || {}).length}/47 variants mapped. Map more variants for smooth edges.`,
        });
      }
    }

    // Check wall sets have minimum directional tiles
    for (const [id, ws] of this._wallSets) {
      const has = ['top', 'bottom', 'left', 'right'].filter(d => ws[d] != null).length;
      if (has < 4) {
        missing.push({
          type: 'wall_incomplete',
          message: `Wall set "${id}" only has ${has}/4 directional tiles. Need top, bottom, left, right at minimum.`,
        });
      }
    }

    return missing;
  }

  // ─── Load Existing ────────────────────────────────────────────

  _loadExisting(meta) {
    const data = meta instanceof TileMeta ? meta.toJSON() : meta;

    for (const tg of (data.terrainGroups || [])) this._terrainGroups.set(tg.id, tg);
    for (const ws of (data.wallSets || [])) this._wallSets.set(ws.id, ws);
    for (const ft of (data.floorTypes || [])) this._floorTypes.set(ft.id, ft);
    for (const obj of (data.objects || [])) this._objects.set(obj.id, obj);
    for (const dec of (data.decorations || [])) this._decorations.set(dec.id, dec);
    for (const op of (data.openings || [])) this._openings.set(op.id, op);

    if (data.tileRoles) {
      for (const [id, role] of Object.entries(data.tileRoles)) {
        this._tileRoles.set(Number(id), role);
      }
    }
    if (data.tileTags) {
      for (const [id, tags] of Object.entries(data.tileTags)) {
        this._tileTags.set(Number(id), new Set(tags));
      }
    }
  }
}
